import { forwardRef, useEffect, createElement, cloneElement } from 'react';
import cn from 'classnames';
import { Icon } from '../Icon/Icon.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { getInputClasses, getButtonStateClasses } from './util.js';

const Input = /*#__PURE__*/forwardRef(function Input(props, ref) {
  const {
    button,
    icon,
    highlighted,
    invalid,
    onKeyDown,
    autoFocus,
    ...otherProps
  } = props;
  const inputRef = useProxiedRef(ref);
  const hasContainer = button || icon;
  const className = cn(getInputClasses(props), 'min-h-[theme(spacing.8)] pointer-events-all', {
    'pr-8': !!hasContainer
  }, !hasContainer && otherProps.className);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, []); // home and end keys only navigate to the start/end of input value if the input container does not scroll
  // if it has scroll height then the browser reverts to native scrolling behaviour only
  // so we manually override it to ensure _our_ desired behaviour remains intact

  const handleKeyDown = event => {
    if (!event.shiftKey && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      const position = event.key === 'End' ? event.currentTarget.value.length : 0;
      event.currentTarget.setSelectionRange(position, position);
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const input = createElement("input", Object.assign({}, otherProps, {
    className: className,
    "data-taco": "input",
    onKeyDown: handleKeyDown,
    ref: inputRef
  }));

  if (hasContainer) {
    let extra;

    if (button) {
      const disabled = button.props.disabled || otherProps.disabled;
      const buttonClassName = cn('items-center flex justify-center border absolute rounded-l-none rounded-r right-0 h-full focus:rounded focus:outline-none', {
        [getButtonStateClasses(invalid)]: !props.disabled
      }, button.props.className);
      extra = cloneElement(button, {
        className: buttonClassName,
        disabled
      });
    } else if (icon) {
      const iconClassName = cn('items-center flex justify-center absolute pointer-events-none mr-1 p-px right-0 w-5 top-1/2 -translate-y-1/2', {
        'text-grey-dark': props.disabled,
        'text-grey-darkest': !props.disabled
      });
      extra = typeof icon === 'string' ? createElement(Icon, {
        className: iconClassName,
        name: icon
      }) : cloneElement(icon, {
        className: cn(iconClassName, icon.props.className)
      });
    }

    const containerClassName = cn('bg-white inline-flex relative rounded w-full', otherProps.className);
    return createElement("div", {
      className: containerClassName,
      "data-taco": "input-container"
    }, input, extra);
  }

  return input;
});

export { Input };
//# sourceMappingURL=Input.js.map
